/**
 * The Expertises list (constitution Article III, R1):
 * `AdminManagedSkillView` rows from `/v1/admin/managed-skills`. Not
 * `AdminSkillView` from `/v1/admin/skills`, the Teammate Builder search,
 * which the teammates feature holds as `skillSearchResults`.
 */
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { matchesSearch } from '@shared';

import * as fromGenerated from '../../_generated';
import { AssistantActions } from '../../chats/assistants/store/assistants.actions';
import {
  AdminManagedSkillRow,
  AdminManagedSkillsActions,
  AdminManagedSkillsSortColumn,
  AdminManagedSkillsSortDirection,
} from './admin-managed-skills.actions';

export const adminManagedSkillsFeatureKey = 'adminManagedSkills';

export interface AdminManagedSkillsSort {
  column: AdminManagedSkillsSortColumn | null;
  direction: AdminManagedSkillsSortDirection;
}

export interface AdminManagedSkillsState extends EntityState<AdminManagedSkillRow> {
  loaded: boolean;
  isLoading: boolean;
  error: string | null;
  sort: AdminManagedSkillsSort;
  filter: string;
  /**
   * Lazily-loaded per-row expand payload for the count-cell dropdowns. An
   * accepted payload also sets its row's `teammateCount` and
   * `directShareCount`; the list load is the only other writer of them.
   */
  dropdownPayloads: Record<
    string,
    fromGenerated.ManagedSkillDropdownPayloadView
  >;
  /** Skills whose last dropdown request failed; the menu says so. */
  dropdownErrors: Record<string, true>;
  /**
   * Bumped by every list load. A dropdown response carries the generation
   * its request started under, and one from an earlier load is dropped, so
   * a request in flight across a reload cannot write back pre-save rows.
   * The list loads on page entry, on every builder close, after a 404 on a
   * dropdown payload and after a failed Supprimer; this guard
   * covers a dropdown request in flight across any of them.
   */
  payloadGeneration: number;
  /**
   * Rows whose Supprimer request is in flight: the menu disables that row's
   * option so a second delete of the same skill never fires (the delete
   * route answers 404 on one).
   */
  deletingIds: Record<string, true>;
  /**
   * Skills deleted this session. A list load in flight when a delete
   * succeeds (the failure reload of another row, or a reload started
   * elsewhere) can carry a response read before the archive committed;
   * filtering this set out of every `setAll` keeps the row from coming
   * back. Never cleared: no route unarchives a skill, so a deleted id never
   * legitimately returns.
   */
  deletedIds: Record<string, true>;
  /**
   * The skill whose "Chat par défaut" request is in flight, or null. The menu
   * disables the option on every other row meanwhile, so one designation is
   * in flight at a time.
   */
  designatingSkillId: string | null;
}

const entityAdapter: EntityAdapter<AdminManagedSkillRow> =
  createEntityAdapter<AdminManagedSkillRow>({
    selectId: (skill) => skill.id,
    sortComparer: false, // Keep the API's order: it sorts by resolved name.
  });

export const initialAdminManagedSkillsState: AdminManagedSkillsState =
  entityAdapter.getInitialState({
    loaded: false,
    isLoading: false,
    error: null,
    sort: { column: 'name', direction: 'asc' },
    filter: '',
    dropdownPayloads: {},
    dropdownErrors: {},
    payloadGeneration: 0,
    deletingIds: {},
    deletedIds: {},
    designatingSkillId: null,
  });

/**
 * The admin tables' search rule on the name (a substring); the id only from
 * its start, so a fragment of a UUID does not match unrelated rows.
 */
const matchesFilter = (
  skill: fromGenerated.AdminManagedSkillView,
  filter: string,
): boolean => matchesSearch(filter, [skill.name], [skill.id]);

const omitKey = <T>(
  record: Record<string, T>,
  key: string,
): Record<string, T> => {
  const next = { ...record };
  delete next[key];
  return next;
};

/**
 * A payload's counts, by the list's own rule: an org-wide grant is one share.
 * A decided client copy of the server rule (KD-41); keep it in step with
 * admin-managed-skills.views.ts (directShareCount), admin-managed-skills.service.ts
 * (toAdminManagedSkillView and getDropdownPayload's hasAllGroupAccess).
 */
const countsFromPayload = (
  payload: fromGenerated.ManagedSkillDropdownPayloadView,
): Pick<
  fromGenerated.AdminManagedSkillView,
  'teammateCount' | 'directShareCount'
> => ({
  teammateCount: payload.teammates.count,
  directShareCount:
    payload.users.count +
    payload.groups.count +
    (payload.hasAllGroupAccess ? 1 : 0),
});

const compareManagedSkills = (
  a: fromGenerated.AdminManagedSkillView,
  b: fromGenerated.AdminManagedSkillView,
  column: AdminManagedSkillsSortColumn,
): number => {
  switch (column) {
    // The API's own comparator, so name ascending and the cleared sort (API
    // order) are the same order.
    case 'name':
      return a.name.localeCompare(b.name);
    case 'teammates':
      return a.teammateCount - b.teammateCount;
    case 'shares':
      return a.directShareCount - b.directShareCount;
  }
};

export const adminManagedSkillsFeature = createFeature({
  name: adminManagedSkillsFeatureKey,
  reducer: createReducer(
    initialAdminManagedSkillsState,

    // `loaded` is never reset: a reload keeps the rows on screen (the
    // builder reloads the list on every close; the page shows its loading
    // block only before the first load).
    // `sort` and `filter` are never reset either, so the user keeps their place.
    // Cached payloads go when the reload STARTS, unlike adminTeammates: once
    // the builder reloads the list on close, a payload read before the save
    // is stale.
    // Clearing on success instead would erase a payload fetched during the
    // reload's 1s floor while its dropdown is still open.
    on(AdminManagedSkillsActions.loadManagedSkills, (state) => ({
      ...state,
      isLoading: true,
      error: null,
      dropdownPayloads: {},
      dropdownErrors: {},
      payloadGeneration: state.payloadGeneration + 1,
    })),
    on(
      AdminManagedSkillsActions.loadManagedSkillsSuccess,
      (state, { skills }) =>
        // A deleted id never legitimately returns (no route unarchives a
        // skill), so a stale response racing the delete cannot bring the
        // row back.
        entityAdapter.setAll(
          skills.filter((skill) => !(skill.id in state.deletedIds)),
          {
            ...state,
            loaded: true,
            isLoading: false,
            error: null,
          },
        ),
    ),
    on(
      AdminManagedSkillsActions.loadManagedSkillsFailure,
      (state, { error }) => ({
        ...state,
        error,
        isLoading: false,
      }),
    ),

    on(AdminManagedSkillsActions.setSort, (state, { column, direction }) => ({
      ...state,
      sort: { column, direction },
    })),
    on(AdminManagedSkillsActions.setFilter, (state, { filter }) => ({
      ...state,
      filter,
    })),

    // Reopening a cell retries: the request clears that skill's error.
    on(AdminManagedSkillsActions.loadDropdownPayload, (state, { skillId }) =>
      skillId in state.dropdownErrors
        ? { ...state, dropdownErrors: omitKey(state.dropdownErrors, skillId) }
        : state,
    ),
    // An accepted payload also sets the row's two counts, so the count the
    // row shows (and the sort that reads it) follows the list its cell opens.
    // `updateOne` on an id the adapter does not hold is a no-op.
    on(
      AdminManagedSkillsActions.loadDropdownPayloadSuccess,
      (state, { skillId, payload, generation }) =>
        generation === state.payloadGeneration
          ? entityAdapter.updateOne(
              { id: skillId, changes: countsFromPayload(payload) },
              {
                ...state,
                dropdownPayloads: {
                  ...state.dropdownPayloads,
                  [skillId]: payload,
                },
                dropdownErrors: omitKey(state.dropdownErrors, skillId),
              },
            )
          : state,
    ),
    on(
      AdminManagedSkillsActions.loadDropdownPayloadFailure,
      (state, { skillId, generation }) =>
        generation === state.payloadGeneration
          ? {
              ...state,
              dropdownErrors: { ...state.dropdownErrors, [skillId]: true },
            }
          : state,
    ),

    on(AdminManagedSkillsActions.deleteManagedSkill, (state, { skillId }) => ({
      ...state,
      deletingIds: { ...state.deletingIds, [skillId]: true },
    })),
    on(
      AdminManagedSkillsActions.deleteManagedSkillSuccess,
      (state, { skillId }) =>
        entityAdapter.removeOne(skillId, {
          ...state,
          deletingIds: omitKey(state.deletingIds, skillId),
          deletedIds: { ...state.deletedIds, [skillId]: true },
          dropdownPayloads: omitKey(state.dropdownPayloads, skillId),
          dropdownErrors: omitKey(state.dropdownErrors, skillId),
        }),
    ),
    on(
      AdminManagedSkillsActions.deleteManagedSkillFailure,
      (state, { skillId }) => ({
        ...state,
        deletingIds: omitKey(state.deletingIds, skillId),
      }),
    ),
    on(AdminManagedSkillsActions.setDefaultChat, (state, { skillId }) => ({
      ...state,
      designatingSkillId: skillId,
    })),
    on(AdminManagedSkillsActions.setDefaultChatSuccess, (state, { skill }) => {
      // At most one default. The API returns only the target's row, so the
      // flag is cleared on every other row here.
      const flagged = entityAdapter.map(
        (row) => ({ ...row, isDefaultChat: row.id === skill.id }),
        state,
      );
      const next: AdminManagedSkillsState = {
        ...flagged,
        designatingSkillId: null,
        // The target's shares changed (the org-wide grant), so its cached
        // dropdown payload is stale.
        dropdownPayloads: omitKey(state.dropdownPayloads, skill.id),
        dropdownErrors: omitKey(state.dropdownErrors, skill.id),
      };
      return skill.id in state.deletedIds
        ? next
        : entityAdapter.upsertOne(skill, next);
    }),
    on(AdminManagedSkillsActions.setDefaultChatFailure, (state) => ({
      ...state,
      designatingSkillId: null,
    })),
    // The builder's admin Delete dispatches only the owner flow's success, so
    // the list removes the row here too. Supprimer's effect sends both; the
    // second is a no-op. This feature state is route-provided and outlives the
    // route, so the listener also runs for an owner's delete made on another
    // page later in the session: `deletedIds` then holds a skill that was
    // deleted for real, which is harmless because a deleted id never returns.
    // If a path ever un-archives a skill, revisit this listener.
    on(AssistantActions.deleteAssistantSuccess, (state, { uuid }) =>
      entityAdapter.removeOne(uuid, {
        ...state,
        deletedIds: { ...state.deletedIds, [uuid]: true },
        dropdownPayloads: omitKey(state.dropdownPayloads, uuid),
        dropdownErrors: omitKey(state.dropdownErrors, uuid),
      }),
    ),
  ),
  extraSelectors: ({
    selectAdminManagedSkillsState,
    selectSort,
    selectFilter,
  }) => {
    const entitySelectors = entityAdapter.getSelectors(
      selectAdminManagedSkillsState,
    );
    const selectFilteredSortedManagedSkills = createSelector(
      entitySelectors.selectAll,
      selectSort,
      selectFilter,
      (skills, sort, filter) => {
        const filtered = filter
          ? skills.filter((skill) => matchesFilter(skill, filter))
          : skills;
        // `null` keeps API order, which the API sorts by resolved name, so the
        // cleared state looks like name ascending.
        if (!sort.column) {
          return filtered;
        }
        const sign = sort.direction === 'asc' ? 1 : -1;
        const column = sort.column;
        // Equal names stay in id order in both directions, as in the API.
        return [...filtered].sort(
          (a, b) =>
            sign * compareManagedSkills(a, b, column) ||
            (column === 'name' ? a.id.localeCompare(b.id) : 0),
        );
      },
    );
    // Over every row, so the search filter never hides the default from the
    // replacement modal.
    const selectDefaultChatSkill = createSelector(
      entitySelectors.selectAll,
      (rows) => rows.find((row) => row.isDefaultChat === true) ?? null,
    );
    return {
      ...entitySelectors,
      selectFilteredSortedManagedSkills,
      selectDefaultChatSkill,
    };
  },
});
